import { useCallback } from 'react';

import { ASSEMBLE_OPTION_KEY, DEFAULT_PRODUCT_QUANTITY, GET_AVAILABILITY_STORES_TYPE } from '@utils/constant';
import useGetAvailabilityStores from '@utils/customsHook/useGetAvailabilityStores';
import { useMappingAdditionalOptions } from '@utils/customsHook/useGetAvailabilityStores/useMappingAdditionalOptions';
import { useMappingFreeGiftData } from '@utils/customsHook/useGetAvailabilityStores/useMappingFreeGiftData';
import useMappingProductsList, {
  mappingProductsListPayload
} from '@utils/customsHook/useGetAvailabilityStores/useMappingProductsList';
import { deepCopy } from '@utils/utility';

export const useCheckEnableBundleBOPISOption = () => {
  return useCallback(
    /** @param {Object} payload
     * @param {Array} payload.bundleProductsList
     * @param {Array} payload.bundleFreeGiftsList*/
    ({
      bundleProductsList,
      bundleFreeGiftsList,
      allowedOptions
    }) => {
      return bundleProductsList.every((productItem) => productItem.HasBOPISOption)
        && (
          bundleFreeGiftsList?.length
            ? bundleFreeGiftsList.some((freeGift) => freeGift.items?.some((freeGiftItem) => freeGiftItem.HasBOPISOption))
            : true
        ) && (
        bundleProductsList.some(productItem => productItem.AdditionalOptions?.find((optionItem) => optionItem?.RelationshipId
            === ASSEMBLE_OPTION_KEY))
          ? !allowedOptions?.[ASSEMBLE_OPTION_KEY]?.fields?.Required
          : true
      );
    }, []);
};

const useConvertAdditionalOptions = () => {
  const mappingAdditionalOptionsService = useMappingAdditionalOptions();

  return useCallback(
    /** INFO: {Array} bundleProductsList */
    (bundleProductsList) => {
      let convertAdditionalOptionsData = {};
      for (const productItem of bundleProductsList) {
        const additionalOptions = productItem.AdditionalOptions || [];
        if (additionalOptions.length) {
          for (const optionItem of additionalOptions) {
            const objectKey = optionItem.RelationshipId;
            if (convertAdditionalOptionsData[objectKey]) {
              if (optionItem.Products?.length && convertAdditionalOptionsData[objectKey].Products?.length) {
                for (const item of optionItem.Products) {
                  const findProduct = (data) => data?.ProductId === item.ProductId && data.VariantId === item.VariantId;
                  let productExistInResult = convertAdditionalOptionsData[objectKey].Products.find(findProduct);

                  const indexOfProductExistInResult = convertAdditionalOptionsData[objectKey].Products.findIndex(
                    findProduct);

                  if (productExistInResult) {
                    convertAdditionalOptionsData[objectKey].Products[indexOfProductExistInResult] = {
                      ...convertAdditionalOptionsData[objectKey].Products[indexOfProductExistInResult],
                      BOPISThreshold: convertAdditionalOptionsData[objectKey].Products[indexOfProductExistInResult].BOPISThreshold
                        + 1
                    };
                  } else {
                    convertAdditionalOptionsData[objectKey].Products.push({ ...item });
                  }
                }
              }
            } else {
              convertAdditionalOptionsData[objectKey] = {
                ...optionItem
              };
            }
          }
        }
      }

      return {
        rawData: convertAdditionalOptionsData,
        convertedData: mappingAdditionalOptionsService(Object.keys(convertAdditionalOptionsData)
          .filter(key => convertAdditionalOptionsData[key].Required).reduce((additionalOptions, key) => {
            additionalOptions[key] = convertAdditionalOptionsData[key];

            return additionalOptions;
          }, {}))
      };
    }, [mappingAdditionalOptionsService]);
};

export const useGetBundleAvailabilityStores = () => {
  const mappingBundleProductsList = useMappingProductsList();
  const checkEnableBundleBOPIS = useCheckEnableBundleBOPISOption();
  const getAvailabilityStoresService = useGetAvailabilityStores();
  const mappingFreeGiftService = useMappingFreeGiftData();
  const convertAdditionalOptionsDataService = useConvertAdditionalOptions();

  return useCallback(
    /** @param {Object} payload
     * @param {Array} payload.bundleProductsList
     * @param {Array} payload.bundleFreeGiftsList
     * @param {Array} [payload.allowedOptions]
     * @param {Array} [payload.freeGiftSelected]*/
    async ({
      bundleProductsList,
      bundleFreeGiftsList,
      allowedOptions,
      freeGiftSelected
    }) => {
      const isEnableBOPISOption = checkEnableBundleBOPIS({
        bundleProductsList,
        bundleFreeGiftsList,
        allowedOptions
      });
      const productsList = mappingBundleProductsList(deepCopy(bundleProductsList));
      const freeGiftsList = mappingFreeGiftService(deepCopy(bundleFreeGiftsList));
      const additionalOptionsList = convertAdditionalOptionsDataService(deepCopy(bundleProductsList));
      const isAllVariantActive = bundleProductsList.every(item => item?.['Active Variant']?.active);

      const productsListMap = new Map();
      for (const productLine of productsList) {
        productLine.forEach((productLineItem) => {
          const mapKey = productLineItem.ProductId;
          if (!productsListMap.get(mapKey)) {
            productsListMap.set(mapKey, [productLineItem]);
          } else {
            const productsListInMap = productsListMap.get(mapKey);

            const variantExistInList = productsListInMap.find((item) => item.VariantId
              === productLineItem.VariantId);
            if (variantExistInList) {
              variantExistInList.Threshold += 1;

              productsListMap.set(mapKey, productsListInMap);
            } else {
              productsListMap.set(mapKey, [...productsListInMap, productLineItem]);
            }
          }
        });
      }

      const convertProductListMapToArray = isAllVariantActive
        ? [Array.from(productsListMap.values()).flat()]
        : Array.from(productsListMap.values());

      const isSelectedEnoughFreeGift = freeGiftSelected?.length === bundleFreeGiftsList?.length;
      if (isSelectedEnoughFreeGift && isAllVariantActive) {
        freeGiftSelected.forEach((freeGiftItem) => convertProductListMapToArray[0] = [
          ...convertProductListMapToArray[0],
          {
            ...mappingProductsListPayload({
              productId: freeGiftItem.ProductId || '',
              variantId: freeGiftItem.VariantId || '',
              BOPISThreshold: freeGiftItem.BOPISThreshold,
              lineQuantity: DEFAULT_PRODUCT_QUANTITY
            })
          }
        ]);
      }

      let isSelectedEnoughAdditionalOptions = bundleProductsList.every((productItem) => {
        return productItem.AdditionalOptions?.every(option => allowedOptions?.[option?.RelationshipId]?.fields?.Required
          ? option.Products?.some((productItem) => productItem.IsChoseAdditionalOption)
          : true);
      });
      if (isSelectedEnoughAdditionalOptions) {
        let selectedOption = [];
        for (const productItem of bundleProductsList) {
          if (productItem.AdditionalOptions?.length) {
            productItem.AdditionalOptions.forEach(optionItem => {
              if (optionItem.Products?.length) {
                optionItem.Products.forEach((optionProductItem) => {
                  if (optionProductItem.IsChoseAdditionalOption) {
                    let optionExistInList = selectedOption.find((selectedOptionItem) => selectedOptionItem.RelationshipId
                      === optionItem.RelationshipId);

                    if (!optionExistInList) {
                      selectedOption.push({
                        ...optionProductItem,
                        RelationshipId: optionItem.RelationshipId
                      });
                    } else {
                      if (optionExistInList.ProductId === optionProductItem.ProductId
                        && optionExistInList.VariantId === optionProductItem.VariantId) {
                        optionExistInList.BOPISThreshold += 1;
                      } else {
                        selectedOption.push({
                          ...optionProductItem,
                          RelationshipId: optionItem.RelationshipId
                        });
                      }
                    }
                  }
                });
              }
            });
          }
        }

        for (const productData of selectedOption) {
          convertProductListMapToArray[0] = [
            ...convertProductListMapToArray[0],
            {
              ...mappingProductsListPayload({
                productId: productData.ProductId || '',
                variantId: '',
                BOPISThreshold: productData.BOPISThreshold,
                lineQuantity: DEFAULT_PRODUCT_QUANTITY
              })
            }
          ];
        }
      }

      const getStoreType = (
        isAllVariantActive || (
          isSelectedEnoughFreeGift && isAllVariantActive && isSelectedEnoughAdditionalOptions
        )
      ) ? GET_AVAILABILITY_STORES_TYPE.And : GET_AVAILABILITY_STORES_TYPE.Or;

      return await getAvailabilityStoresService(
        {
          isEnableBOPISOption,
          productsList: convertProductListMapToArray,
          freeGiftsList: (
            isSelectedEnoughFreeGift && isAllVariantActive
          ) ? [] : freeGiftsList,
          additionalOptions: isSelectedEnoughAdditionalOptions ? [] : additionalOptionsList?.convertedData
        },
        getStoreType
      );
    },
    [mappingFreeGiftService, mappingBundleProductsList, checkEnableBundleBOPIS, convertAdditionalOptionsDataService]
  );
};